// The invitation token, waiting out a sign-in. Per tab, dies with the tab — the same
// posture as the session token itself.
//
// It lives in its own module because two places need it: the invite screen writes and
// redeems it, and the router reads it — a sign-in vendor decides for itself where to
// land after authenticating, and wherever that is, a pending invitation must still get
// redeemed. The first live redemption proved the redirect cannot be trusted to come back:
// the vendor dropped the signed-in person on the plans list and the token sat in the tab,
// unused, looking exactly like a broken invitation.

const STASH_KEY = 'mor.invite.token';

/**
 * Subscribable, exactly as the session store is, and for the same reason: the router
 * reads this value to decide what a signed-in person sees, and a value read once at
 * render is a redirect baked in until something unrelated re-renders — which is how the
 * first version of this bounced a freshly redeemed person back to an empty invite screen.
 */
const listeners = new Set<() => void>();

export function subscribeInviteStash(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function pendingInviteToken(): string | null {
  try {
    return window.sessionStorage.getItem(STASH_KEY);
  } catch {
    return null;
  }
}

export function stashInviteToken(value: string | null): void {
  try {
    if (value === null) {
      window.sessionStorage.removeItem(STASH_KEY);
    } else {
      window.sessionStorage.setItem(STASH_KEY, value);
    }
  } catch {
    // A browser that refuses the store degrades to the fragment-only behavior: the
    // redemption still works whenever a session already exists at the moment the link
    // is followed.
  }
  for (const listener of listeners) {
    listener();
  }
}
